import React, {useState} from 'react';
import { useSelector } from 'react-redux';
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { TextField } from '@mui/material';
import { yupResolver } from "@hookform/resolvers/yup";

import api from "@/services/api";
import PenIcon from '@/assets/icons/simpleTextAdvices/pen_icon.svg';
import CloseIcon from '@/assets/icons/simpleTextAdvices/close_icon.svg';
import WithCharCount from '@/views/components/charCounter/charCounter';

import styles from '@/views/components/SimpleTextAdivse/Comments/Comments.module.scss';
import { CheckPermission } from '@/utils/permissions/permissions';

const defaultValues = {
	content: '',
	contentEdit: '',
};

const TextFieldWithCharCount = WithCharCount(TextField);

const Comments = ({comments, show, questionId, getQuestion, showActionButtons}) => {
	const [ commentEditId, setCommentEditId ] = useState(null); 
	const dict = useSelector(state => state.app.dict);

	const user = useSelector((state) => state.auth.user);

	const userRole = user && user.role;

	const getAnswerAuthor = (commentObj) => {
		if(commentObj?.externalAuthor) {
			return <p className={ styles['comments__author'] }>{commentObj?.externalAuthor?.forename} {commentObj?.externalAuthor?.surname}</p>;
		} else if (commentObj?.author?.displayName) {
			return <p className={ styles['comments__author'] }>{commentObj?.author?.displayName}</p>;
		} else {
			return '';
		}
	};

	const validationSchema = Yup.object().shape({
		content: Yup.string().when('contentEdit', {
			is: (contentEdit) => !contentEdit || contentEdit.length === 0,
			then: () => Yup.string().required(dict.simpleTextAdvices.comments.errorRequaired),
			otherwise: () => Yup.string().notRequired(),
		}),
		contentEdit: Yup.string().notRequired(),
	});

	const { handleSubmit, setValue, control, setError, formState: { errors } } = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: defaultValues,
		reValidateMode: 'onChange',
		mode: 'onChange',
	});

	const handleEditComment = (id, content) => {
		setCommentEditId(id);
		setValue('contentEdit', content);
	};

	const handleDeleteComment = async (id) => {
		const res = await api.delete(`/questions/comments/${id}`);

		if(res.status === 200) {
			getQuestion(questionId);
		}
	};

	const onSubmitEdit = async({contentEdit}) => {
		// setRequestIsPending(true);

		try {
			const res = await api.put(`/questions/${questionId}/comments/${commentEditId}`, {content: contentEdit});

			if(res.status === 200) {
				setCommentEditId(null);
				getQuestion(questionId);
			} else {
				// getErrors(res.data.errors);
			}
		} catch (err) {
			// getErrors(err.response.data.errors);
		} finally {
			// setRequestIsPending(false);
		}
	};

	const onSubmit = async({content}) => {
		// setRequestIsPending(true);
		if(content.length < 1) {
			setError('content', {message: dict.simpleTextAdvices.comments.errorRequaired});
			return;
		}
		try {
			let res = await api.post(`/questions/${questionId}/comments`, {content});

			if(res.status === 200) {
				setCommentEditId(null);
				getQuestion(questionId);
				setValue('content', '');
			} else {
				// getErrors(res.data.errors);
			}
		} catch (err) {
			// getErrors(err.response.data.errors);
		} finally {
			// setRequestIsPending(false);
		}
	};

	return (
		<>
			{show && <div className={ styles['comments'] }>
				<div className={ styles['comments__counter'] }>{dict.simpleTextAdvices.comments.title} ({comments.length})</div>
				{comments && comments.map(comment => 
					(
						<div 
							className={ styles['comments__comment-box'] }
							key={ comment.id }
						>
							<div className={ styles['comments__header'] }>
								{getAnswerAuthor(comment)}<p className={ styles['comments__date'] }>{comment.createdOn.replaceAll('-', '.')}</p>
							</div>
							<p className={ styles['comments__content'] }>{comment.content}</p>
							{((CheckPermission("canManageQuestions", userRole) || comment.author.login === user.login) && showActionButtons) && <div className={ styles['comments__buttons-action'] }>
								<button 
									className='button__transparent'
									onClick={ () => commentEditId === comment.id ? setCommentEditId(null) : handleEditComment(comment.id, comment.content) }>
									<img src={ PenIcon }
										alt="" />{commentEditId === comment.id ? dict.simpleTextAdvices.comments.buttons.close : dict.simpleTextAdvices.comments.buttons.edit}
								</button>
								<button 
									className='button__transparent'
									onClick={ ()=> handleDeleteComment(comment.id) }>
									<img src={ CloseIcon }
										alt="" />
									{dict.simpleTextAdvices.comments.buttons.delete}
								</button>
							</div> }
								
							{commentEditId === comment.id && <form onSubmit={ handleSubmit(onSubmitEdit) }>
								<label 
									className={ styles['comments__add__label'] }
									htmlFor='content'>
									{dict.simpleTextAdvices.comments.addLabel}
								</label>
								<Controller
									render={ ({ field }) => <TextFieldWithCharCount
										size='small'
										fullWidth
										multiline
										maxRows={ 4 }
										placeholder={ dict.simpleTextAdvices.comments.enterCommentPlaceholder }
										className='modal-children__count'
										maxCharCount={ 4000 }
										{ ...field }	  
									/> }
									name={ 'contentEdit' }
									control={ control }
									errors={ errors.content }
								/>
								<div className={ styles['comments__add__btn'] }>
									<button	className='button__blue'>
										{dict.simpleTextAdvices.comments.buttons.editComment}
									</button>
								</div>
							</form>}
						</div>
					),
				)}
				{showActionButtons && 
					<div className={ styles['comments__add__container'] }>
						<p className={ styles['comments__add__info'] }>
							{dict.simpleTextAdvices.comments.info}
						</p>

						<form onSubmit={ handleSubmit(onSubmit) }>
							<label 
								className={ styles['comments__add__label'] }
								htmlFor='content'>
								{dict.simpleTextAdvices.comments.addLabel}
							</label>
							<Controller
								render={ ({ field }) => <TextFieldWithCharCount
									size='small'
									fullWidth
									multiline
									maxRows={ 4 }
									placeholder={ dict.simpleTextAdvices.comments.enterCommentPlaceholder }
									className='modal-children__count'
									maxCharCount={ 4000 }
									{ ...field }	  
								/> }
								name={ 'content' }
								control={ control }
								errors={ errors.content }
							/>
							<p className={ styles["comments__add__error"] }>{errors && errors.content && errors.content.message}</p>
							<div className={ styles['comments__add__btn'] }>
								<button	className='button__blue'>
									{dict.simpleTextAdvices.comments.buttons.addComment}
								</button>
							</div>
						</form>
					</div>}
			</div>}
		</>
	);
};

export default Comments;